/*
  ada-fixes.js : Capsule Hats accessibility remediation layer (added 10/19/26)
  Works alongside Stiletto 2.2.0 theme.min.js without modifying it.
  1. Size / color buttons expose selected (aria-pressed) and sold-out state.
  2. Add to cart is announced in a live region and focus moves to the confirmation popup.
  3. Desktop menu: dropdowns no longer open on keyboard focus (so hidden links stay out of
     the Tab order); a small disclosure button next to each parent link opens them instead.
  4. Cart order note trigger exposes aria-expanded; collapsed note field is inert.
  5. Cart icon label stays in sync with the item count.
*/
(function () {
  'use strict';
  var doc = document;

  function $(sel, ctx) { return (ctx || doc).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || doc).querySelectorAll(sel)); }
  function safe(fn) { try { fn(); } catch (e) { if (window.console) console.warn('[ada-fixes]', e); } }

  /* ---------- live region ---------- */
  function liveRegion() {
    var r = doc.getElementById('AdaLiveRegion');
    if (!r) {
      r = doc.createElement('div');
      r.id = 'AdaLiveRegion';
      r.className = 'visually-hidden';
      r.setAttribute('role', 'status');
      r.setAttribute('aria-live', 'polite');
      r.setAttribute('aria-atomic', 'true');
      doc.body.appendChild(r);
    }
    return r;
  }
  function announce(msg) {
    var r = liveRegion();
    r.textContent = '';
    setTimeout(function () { r.textContent = msg; }, 120);
  }

  /* ---------- 1. option buttons ---------- */
  var OPTION_BTN = '[data-option-buttons] [data-button]';
  function optionValue(btn) {
    var v = btn.getAttribute('data-option-value') || btn.textContent || '';
    var t = doc.createElement('textarea');
    t.innerHTML = v; // decode entities
    return t.value.trim();
  }
  function syncOptionButton(btn) {
    var selected = btn.classList.contains('selected');
    var soldOut = btn.classList.contains('disabled');
    var label = optionValue(btn) + (soldOut ? ', sold out' : '');
    if (btn.getAttribute('aria-pressed') !== String(selected)) btn.setAttribute('aria-pressed', String(selected));
    if (soldOut) { if (btn.getAttribute('aria-disabled') !== 'true') btn.setAttribute('aria-disabled', 'true'); }
    else if (btn.hasAttribute('aria-disabled')) btn.removeAttribute('aria-disabled');
    if (btn.getAttribute('aria-label') !== label) btn.setAttribute('aria-label', label);
  }
  function syncAllOptionButtons(ctx) { $$(OPTION_BTN, ctx).forEach(syncOptionButton); }

  /* ---------- 2. add to cart ---------- */
  var lastAddButton = null;
  var lastAddByKeyboard = false;
  doc.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('[data-add-to-cart]');
    if (b) { lastAddButton = b; lastAddByKeyboard = (e.detail === 0); }
  }, true);
  doc.addEventListener('submit', function (e) {
    var f = e.target;
    if (f && f.matches && f.matches('[data-product-form]')) {
      var b = $('[data-add-to-cart]', f);
      if (b) lastAddButton = b;
    }
  }, true);

  function updateCartLabels(count) {
    if (typeof count !== 'number') return;
    $$('[data-js-cart-icon]').forEach(function (a) {
      a.setAttribute('aria-label', 'Cart, ' + count + (count === 1 ? ' item' : ' items'));
    });
  }

  doc.addEventListener('cart:item-added', function (evt) {
    safe(function () {
      var p = (evt.detail && evt.detail.product) || {};
      var name = p.product_title || p.title || 'Item';
      var variant = p.variant_title && !/default title/i.test(p.variant_title) ? ', ' + p.variant_title : '';
      var msg = 'Added to cart: ' + name + variant + '.';
      var status = $('[data-product-status]');
      fetch((window.theme && theme.routes && theme.routes.cart && theme.routes.cart.base ? theme.routes.cart.base : '/cart') + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var full = msg + ' Cart has ' + cart.item_count + (cart.item_count === 1 ? ' item.' : ' items.');
          if (status) { status.textContent = full; } else { announce(full); }
          updateCartLabels(cart.item_count);
        })
        .catch(function () { if (status) { status.textContent = msg; } else { announce(msg); } });
    });
  });
  doc.addEventListener('cart:updated', function (evt) {
    safe(function () { if (evt.detail && evt.detail.cart) updateCartLabels(evt.detail.cart.item_count); });
  });
  doc.addEventListener('cart:error', function (evt) {
    safe(function () {
      var m = evt.detail && evt.detail.errorMessage;
      announce('Could not add to cart. ' + (typeof m === 'string' ? m : ''));
    });
  });

  function prepConfirmationPopup(popup) {
    if (!popup.hasAttribute('role')) popup.setAttribute('role', 'dialog');
    popup.setAttribute('aria-modal', 'false');
    popup.setAttribute('aria-label', 'Added to cart');
    if (!popup.hasAttribute('tabindex')) popup.setAttribute('tabindex', '-1');
  }
  function onConfirmationOpened(popup) {
    prepConfirmationPopup(popup);
    if (!lastAddByKeyboard) return; // mouse users keep the theme's normal behavior
    var inner = $('.purchase-confirmation-popup__inner', popup) || popup;
    // Keep the popup open while a keyboard / screen reader user is inside it
    // (the theme auto-closes after 5 seconds unless hovered).
    safe(function () { inner.dispatchEvent(new MouseEvent('mouseover', { bubbles: true })); });
    var heading = $('.purchase-confirmation-popup__heading', popup);
    if (heading) {
      if (!heading.id) heading.id = 'PurchaseConfirmationHeading';
      popup.removeAttribute('aria-label');
      popup.setAttribute('aria-labelledby', heading.id);
    }
    setTimeout(function () { try { popup.focus({ preventScroll: true }); } catch (e) { popup.focus(); } }, 50);
  }
  function onConfirmationClosed(popup) {
    if (popup.contains(doc.activeElement) || doc.activeElement === doc.body) {
      if (lastAddButton && doc.contains(lastAddButton)) lastAddButton.focus();
    }
  }
  doc.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.keyCode !== 27) return;
    var popup = doc.activeElement && doc.activeElement.closest && doc.activeElement.closest('.purchase-confirmation-popup.active');
    if (popup) {
      var close = $('[data-confirmation-close]', popup);
      if (close) { e.preventDefault(); close.click(); }
    }
  });

  /* ---------- 3. desktop menu ---------- */
  var allowMenuFocusOpen = false;
  function isTopTrigger(el) {
    return el && el.matches && el.matches('.header__links-list > li > [data-dropdown-trigger], .header__links-list > li > [data-meganav-trigger]');
  }
  // Stop the theme from opening dropdowns just because a top-level link got keyboard focus.
  doc.addEventListener('focus', function (e) {
    if (!allowMenuFocusOpen && isTopTrigger(e.target)) {
      e.stopPropagation();
      closeAllMenus(e.target);
    }
  }, true);

  function escapeEvent() {
    var ev = new KeyboardEvent('keydown', { key: 'Escape', bubbles: true });
    try { Object.defineProperty(ev, 'keyCode', { get: function () { return 27; } }); } catch (x) {}
    return ev;
  }
  function closeAllMenus(fromEl) {
    var nav = fromEl && fromEl.closest ? fromEl.closest('.header__links-list') : $('.header__links-list');
    var root = nav ? (nav.closest('[data-section-type="header"]') || nav.parentNode) : null;
    var anyOpen = root && $('[data-submenu].active, .meganav.active', root);
    if (anyOpen && nav) nav.dispatchEvent(escapeEvent());
    $$('.ada-nav-toggle[aria-expanded="true"]').forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
  }
  function submenuFor(trigger) {
    var li = trigger.parentNode;
    if (trigger.hasAttribute('data-meganav-trigger')) {
      var h = trigger.getAttribute('data-meganav-handle');
      var header = trigger.closest('[data-section-type="header"]') || doc;
      return $('.meganav[data-menu-handle="' + h + '"]', header) || $('.meganav', li);
    }
    return $('[data-submenu]', li);
  }
  function setupMenuToggles() {
    $$('.header__links-list > li > [data-dropdown-trigger], .header__links-list > li > [data-meganav-trigger]').forEach(function (trigger) {
      if (trigger.nextElementSibling && trigger.nextElementSibling.classList.contains('ada-nav-toggle')) return;
      var titleEl = $('.link-hover', trigger);
      var title = (titleEl ? titleEl.textContent : trigger.textContent).trim();
      var btn = doc.createElement('button');
      btn.type = 'button';
      btn.className = 'ada-nav-toggle';
      btn.setAttribute('aria-expanded', 'false');
      var sub = submenuFor(trigger);
      if (sub && sub.id) btn.setAttribute('aria-controls', sub.id);
      btn.setAttribute('aria-label', title + ' submenu');
      btn.innerHTML = '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="10" height="10"><path d="M1.875 7.438 12 17.563 22.125 7.438" stroke="currentColor" stroke-width="2" fill="none"></path></svg>';
      trigger.parentNode.insertBefore(btn, trigger.nextSibling);
      // The trigger link itself is a real link; it should not claim to be a popup button.
      trigger.removeAttribute('aria-haspopup');
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var s = submenuFor(trigger);
        var open = s && s.classList.contains('active');
        if (open) {
          closeAllMenus(trigger);
          btn.setAttribute('aria-expanded', 'false');
        } else {
          closeAllMenus(trigger);
          trigger.dispatchEvent(new MouseEvent('mouseover', { bubbles: true }));
          btn.setAttribute('aria-expanded', 'true');
        }
      });
      btn.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { closeAllMenus(trigger); btn.setAttribute('aria-expanded', 'false'); btn.focus(); }
      });
    });
    // Close an open dropdown when keyboard focus moves to another top-level item or leaves it.
    doc.addEventListener('focusin', function (e) {
      var t = e.target;
      $$('.ada-nav-toggle[aria-expanded="true"]').forEach(function (btn) {
        var trigger = btn.previousElementSibling;
        var s = trigger && submenuFor(trigger);
        if (t === btn || (s && s.contains(t))) return;
        closeAllMenus(trigger);
        btn.setAttribute('aria-expanded', 'false');
      });
    });
    // Escape inside an open dropdown returns focus to its toggle (capture phase, before the theme closes it).
    doc.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.keyCode !== 27) return;
      var a = doc.activeElement;
      $$('.ada-nav-toggle').forEach(function (btn) {
        var s = submenuFor(btn.previousElementSibling);
        if (s && s.contains(a)) {
          setTimeout(function () { closeAllMenus(btn); btn.setAttribute('aria-expanded', 'false'); btn.focus(); }, 0);
        }
      });
    }, true);
  }

  /* ---------- 4. cart order note ---------- */
  function syncOrderNote(input) {
    var wrapper = input.parentNode;
    var trigger = wrapper && $('[data-order-note-trigger]', wrapper);
    var open = input.getAttribute('aria-expanded') === 'true' ||
      (input.getAttribute('aria-hidden') !== 'true' && input.offsetHeight > 0 && getComputedStyle(input).display !== 'none' && getComputedStyle(input).visibility !== 'hidden');
    if (trigger) trigger.setAttribute('aria-expanded', String(open));
    if (open) { input.removeAttribute('inert'); input.removeAttribute('aria-hidden'); }
    else { input.setAttribute('inert', ''); }
  }
  function syncAllOrderNotes(ctx) { $$('[cart-note-input]', ctx).forEach(syncOrderNote); }

  /* ---------- observers ---------- */
  function observe() {
    var mo = new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        var t = m.target;
        if (m.type === 'attributes') {
          if (t.matches && t.matches(OPTION_BTN) && m.attributeName === 'class') syncOptionButton(t);
          else if (t.hasAttribute && t.hasAttribute('cart-note-input') && (m.attributeName === 'aria-expanded' || m.attributeName === 'aria-hidden' || m.attributeName === 'style')) syncOrderNote(t);
          else if (t.classList && t.classList.contains('purchase-confirmation-popup') && m.attributeName === 'class') {
            var was = (m.oldValue || '').split(/\s+/).indexOf('active') > -1;
            var is = t.classList.contains('active');
            if (is && !was) onConfirmationOpened(t);
            if (!is && was) onConfirmationClosed(t);
          } else if (t.matches && t.matches('[data-submenu], .meganav') && m.attributeName === 'class') {
            $$('.ada-nav-toggle').forEach(function (btn) {
              var s = submenuFor(btn.previousElementSibling);
              if (s === t) btn.setAttribute('aria-expanded', String(t.classList.contains('active')));
            });
          }
        } else if (m.type === 'childList' && m.addedNodes.length) {
          m.addedNodes.forEach(function (n) {
            if (n.nodeType !== 1) return;
            syncAllOptionButtons(n.parentNode || n);
            syncAllOrderNotes(n.parentNode || n);
            if (n.matches && n.matches('.purchase-confirmation-popup')) prepConfirmationPopup(n);
          });
        }
      });
    });
    mo.observe(doc.body, { subtree: true, childList: true, attributes: true, attributeOldValue: true, attributeFilter: ['class', 'aria-expanded', 'aria-hidden', 'style'] });
  }


  /* ADA 10/19 round 2: keep heading levels sequential (WCAG 1.3.1 / axe heading-order).
     Only lowers the exposed level via aria-level; visual styling is unchanged. */
  function fixHeadingOrder() {
    var hs = $$('h1,h2,h3,h4,h5,h6,[role="heading"]');
    var prev = 0;
    hs.forEach(function (h) {
      if (h.closest('[aria-hidden="true"]')) return;
      var orig = h.getAttribute('data-ada-level');
      if (!orig) {
        orig = h.getAttribute('aria-level') || (/^H[1-6]$/.test(h.tagName) ? h.tagName.charAt(1) : '2');
        h.setAttribute('data-ada-level', orig);
      }
      var lvl = parseInt(orig, 10);
      if (prev && lvl > prev + 1) lvl = prev + 1;
      if (String(lvl) !== orig || h.hasAttribute('aria-level')) h.setAttribute('aria-level', String(lvl));
      var cs = getComputedStyle(h);
      var shown = h.getClientRects().length && cs.visibility !== 'hidden' && !h.closest('[inert]');
      if (shown) prev = lvl;
    });
  }
  var headingTimer;
  function fixPolicyH1() {
    var hs = doc.querySelectorAll('.shopify-policy__body h1, .shopify-policy__container .rte h1');
    for (var i = 0; i < hs.length; i++) { var h = hs[i], n = doc.createElement('h2'); for (var a = 0; a < h.attributes.length; a++) n.setAttribute(h.attributes[a].name, h.attributes[a].value); while (h.firstChild) n.appendChild(h.firstChild); h.parentNode.replaceChild(n, h); }
  }
  function nameEmptyLinks() {
    var as = doc.querySelectorAll('a[href]');
    for (var i = 0; i < as.length; i++) {
      var a = as[i];
      if ((a.getAttribute('aria-label') || '').trim() || (a.textContent || '').trim()) continue;
      var imgs = a.querySelectorAll('img[alt]'), alt = '';
      for (var j = 0; j < imgs.length; j++) alt += (imgs[j].alt || '').trim();
      if (alt) continue;
      var href = (a.getAttribute('href') || '').split('?')[0].split('#')[0];
      var slug = href.split('/').filter(Boolean).pop() || 'home';
      var label = slug.replace(/[-_]+/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
      if (href.indexOf('/collections/') !== -1) label = 'Shop ' + label;
      a.setAttribute('aria-label', label);
    }
  }
  function scheduleHeadingFix() { clearTimeout(headingTimer); headingTimer = setTimeout(function () { safe(fixPolicyH1); safe(nameEmptyLinks); safe(fixHeadingOrder); safe(fixThirdParty); safe(labelAppInputs); safe(dedupeIds); }, 150); }


  /* ADA 10/19 round 2: Pre-order Now app modals + mobile drawer nav label */
  function fixThirdParty() {
    $$('.drawer-menu__main[role="navigation"]').forEach(function (n) { if (!n.getAttribute('aria-label')) n.setAttribute('aria-label', 'Mobile menu'); });
    var mw = doc.getElementById('pn-mixed-cart-warning-content');
    if (mw && !mw.hasAttribute('role')) { mw.setAttribute('role', 'dialog'); mw.setAttribute('aria-modal', 'true'); mw.setAttribute('aria-labelledby', 'pn-mixed-cart-warning-title'); mw.setAttribute('aria-describedby', 'pn-mixed-cart-warning-text'); mw.setAttribute('tabindex', '-1'); }
    var x = doc.getElementById('pn-modal-close-btn');
    if (x && !x.hasAttribute('role')) {
      x.setAttribute('role', 'button'); x.setAttribute('tabindex', '0'); x.setAttribute('aria-label', 'Close');
      x.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); x.click(); } });
    }
    $$('.pn-bis-card').forEach(function (cd) { if (!cd.hasAttribute('role')) { cd.setAttribute('role', 'dialog'); cd.setAttribute('aria-modal', 'true'); cd.setAttribute('aria-label', 'Notify me when available'); } });
    $$('.pn-bis-close').forEach(function (b) { if (!b.getAttribute('aria-label') && !b.textContent.trim()) b.setAttribute('aria-label', 'Close'); });
  }
  /* ADA 10/29: Pre-order Now notify inputs have only placeholders. Give them accessible names. */
  function labelAppInputs() {
    var names = { 'notify-pn-bk-stock-input-name': 'Your name', 'notify-pn-bk-stock-input-email': 'Email address', 'notify-pn-bk-stock-input-phone': 'Phone number' };
    Object.keys(names).forEach(function (c) {
      $$('input.' + c).forEach(function (i) { if (!i.getAttribute('aria-label') && !(i.id && doc.querySelector('label[for="' + i.id + '"]'))) i.setAttribute('aria-label', names[c]); });
    });
    /* Third-party iframes (SocialSnowball affiliate portal and similar) arrive without a title */
    $$('iframe:not([title])').forEach(function (f) {
      var s = f.getAttribute('src') || '';
      f.setAttribute('title', /socialsnowball/i.test(s) ? 'Affiliate program sign up' : /youtube/i.test(s) ? 'YouTube video' : 'Embedded content');
    });
  }

  /* ADA 10/29: duplicate ids. The app modal is injected twice, and some theme blocks (filters, description accordion)
     render a mobile and a desktop copy with the same id. Remove the extra app modal and rename later copies,
     updating aria-controls, aria-labelledby and label[for] references that live next to them. */
  function dedupeIds() {
    var mods = $$('[id="pn-mixed-cart-warning"]');
    for (var m = 1; m < mods.length; m++) mods[m].parentNode.removeChild(mods[m]);
    var seen = {};
    $$('[id]').forEach(function (el) {
      var id = el.id; if (!id) return;
      if (!seen[id]) { seen[id] = 1; return; }
      seen[id]++;
      var nid = id + '--' + seen[id];
      var scope = el.parentElement, refs = [];
      for (var k = 0; k < 6 && scope; k++, scope = scope.parentElement) {
        refs = $$('[aria-controls="' + id + '"], [aria-labelledby="' + id + '"], [aria-describedby="' + id + '"], label[for="' + id + '"]', scope);
        if (refs.length) break;
      }
      el.id = nid;
      refs.forEach(function (r) {
        ['aria-controls', 'aria-labelledby', 'aria-describedby', 'for'].forEach(function (a) { if (r.getAttribute(a) === id) r.setAttribute(a, nid); });
      });
    });
  }

  doc.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var x = doc.getElementById('pn-modal-close-btn');
    var m = doc.getElementById('pn-mixed-cart-warning');
    if (x && m && m.getClientRects().length && getComputedStyle(m).display !== 'none') x.click();
    if (typeof window.closeNotifyEmailPopup === 'function') { var bd = doc.querySelector('.pn-bis-dialog'); if (bd && bd.getClientRects().length && getComputedStyle(bd).display !== 'none') window.closeNotifyEmailPopup(); }
  });

  function init() {
    safe(function () { syncAllOptionButtons(doc); });
    safe(function () { syncAllOrderNotes(doc); });
    safe(function () { $$('.purchase-confirmation-popup').forEach(prepConfirmationPopup); });
    safe(setupMenuToggles);
    safe(observe);
    safe(function () { liveRegion(); });
    safe(fixPolicyH1);
    safe(nameEmptyLinks);
    safe(fixHeadingOrder);
    safe(fixThirdParty);
    safe(labelAppInputs);
    safe(dedupeIds);
    safe(function () { new MutationObserver(function (ms) { for (var i = 0; i < ms.length; i++) { if (ms[i].addedNodes.length) { scheduleHeadingFix(); return; } } }).observe(doc.body, { childList: true, subtree: true }); });
    window.addEventListener('load', scheduleHeadingFix);
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init); else init();
})();
